import { type CSSProperties, useMemo } from "react";
import RedemptionHeader from "@/components/giftExperience/RedemptionHeader";
import RedemptionCollection from "@/components/redemption/RedemptionCollection";
import RedemptionHero from "@/components/redemption/RedemptionHero";
import RedemptionPledgeBanner from "@/components/redemption/RedemptionPledgeBanner";
import { LANDING_PREVIEW_DETAIL_Z } from "@/constants/campaignBuilder";
import type { BeneficiaryCause } from "@/constants/landing";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useProductDetail } from "@/context/ProductDetailContext";
import { useCharityCauses } from "@/hooks/useCharityCauses";
import type { CauseSelection, CharityCause } from "@/interfaces/charity";
import type { RedemptionConfig } from "@/interfaces/redemption";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";
import {
  manualPreviewContact,
  resolveAllTokens,
  resolvePreviewContact,
} from "@/utils/emailPreview-helper";

interface LandingPreviewProps {
  isMobile: boolean;
  /** Opens the gift-message modal (owned by DesignStep, above the preview). */
  onReadMessage: () => void;
  /** Opens the gift video modal (owned by DesignStep). */
  onPlayVideo: () => void;
  /** Set only inside FullscreenPreviewOverlay — raises the product detail drawer above it. */
  isFullscreen?: boolean;
}

/**
 * Campaign-builder Landing Page preview — WYSIWYG with the real recipient
 * `/g/:token` landing. Reuses the shipped recipient components (header → hero →
 * collection → pledge), fed from the builder context: gifts from step 1
 * (`savedGifts`), country from step 1 (`selectedCountry`), the Landing tab's own
 * settings, and the step-4 beneficiary causes. Pro custom colors are applied here —
 * accent via `--lp-accent` on this wrapper (the shared filter/card accents read it),
 * hero background + text via RedemptionHero props. No footer (prototype parity).
 */
export default function LandingPreview({
  isMobile,
  onReadMessage,
  onPlayVideo,
  isFullscreen = false,
}: LandingPreviewProps) {
  const {
    stage3Landing: landing,
    stage3Email,
    stage3Unboxing,
    savedGifts,
    selectedCountry,
    selectedCauses,
    rolloverEnabled,
    recipients,
    mockToken,
    notificationMode,
    linkToList,
    senderFirstName,
    senderLastName,
    senderCompany,
  } = useCampaignBuilder();

  // An ANONYMOUS-links campaign (manual Path A, or any universal) has no recipient rows, so its
  // @Sender* / @Signature tokens in the hero copy resolve from the Step 2 "Who are these gifts from?"
  // fields. A manual "link to a list" campaign (Path B) has real recipients like gfg — leave manualSender
  // undefined so the hero copy's @FirstName / @LastName / @Sender* resolve from the first populated row.
  const manualSender = isAnonymousLinks(notificationMode, linkToList)
    ? { firstName: senderFirstName, lastName: senderLastName, company: senderCompany }
    : undefined;
  const { openProductDetail } = useProductDetail();
  const { data: charityCauses = [] } = useCharityCauses();

  // Landing logo mirrors the email logo while synced (default); once unlinked it uses the
  // landing's own image. The banner is always the landing's own.
  const landingLogoUrl = landing.logoSynced ? stage3Email.logoUrl : landing.logoUrl;

  const config: RedemptionConfig = {
    showGiftMessage: stage3Unboxing.showGiftMessage,
    showBanner: landing.bannerEnabled,
    showVideo: landing.showVideo,
    showLogo: landing.logoEnabled,
    skipUnboxing: stage3Unboxing.skipUnboxing,
    logoUrl: landingLogoUrl,
    bannerUrl: landing.bannerUrl,
    videoUrl: landing.videoUrl,
    envelopeColor: landing.envelopeColor,
    donateUnredeemed: false,
  };

  // Accent (Pro) — exposed as --lp-accent, which the shared active filter tab +
  // product-card cause read via var(--lp-accent, <default>). The recipient flow never
  // sets it, so those elements keep their defaults there (recipient-safe).
  const accentStyle = { "--lp-accent": landing.accentColor } as CSSProperties;

  // Pledge banner (step 4): resolve the featured cause's display data from the charity catalogue
  // (selectedCauses only carries {causeProductId, share}) — see toFeaturedCause for the rule.
  const featuredCause = useMemo<BeneficiaryCause | undefined>(
    () => toFeaturedCause(selectedCauses, charityCauses),
    [selectedCauses, charityCauses],
  );
  // Hero copy is personalizable (Landing → Message inserts @tokens), so the preview resolves it
  // the same way the recipient landing does — server-side `resolveRedemptionTokens`.
  const resolveHeroCopy = (copy: string) =>
    resolveAllTokens(copy, stage3Email.signatureHtml || "", recipients, manualSender);

  // The pledge banner's sender line: manual pulls the campaign-level sender, gfg the first row that
  // actually filled each column (row 0 may be blank while a later row carries the sender).
  const previewContact = manualSender
    ? manualPreviewContact(manualSender)
    : resolvePreviewContact(recipients);
  const sender = {
    firstName: previewContact.senderFirst || "Acme",
    lastName: previewContact.senderLast,
    companyName: previewContact.senderCompany || "Acme Corp",
  };

  return (
    <div className="bg-white" style={accentStyle}>
      {/* No "Shipping to" picker here — in the builder, country is driven by the
          fullscreen preview's own country control (the header pill is recipient-only).
          The inline preview pins the logo size to its frame; fullscreen fills the viewport,
          so it follows the breakpoint like the recipient landing. */}
      <RedemptionHeader
        partnerLogoUrl={landing.logoEnabled ? landingLogoUrl || undefined : undefined}
        partnerLogoAlt="Partner"
        isMobile={isFullscreen ? undefined : isMobile}
      />

      <RedemptionHero
        config={config}
        isMobile={isMobile}
        onReadMessage={onReadMessage}
        onPlayVideo={onPlayVideo}
        headline={resolveHeroCopy(landing.headline)}
        subtitle={resolveHeroCopy(landing.subtitle)}
        bgColor={landing.heroBgColor}
        textMode={landing.heroTextMode}
      />

      {/* Prefer the live Step-1 grid (unsaved edits); when it isn't loaded — e.g. the user
          landed straight on the Landing step without visiting Step 1 — fall back to the saved
          campaign feed via the mock token, so a selected campaign's products always show. */}
      <RedemptionCollection
        isMobile={isMobile}
        products={savedGifts ?? undefined}
        token={savedGifts ? undefined : mockToken || undefined}
        activeCountry={selectedCountry}
        onOpenProduct={(id) => openProductDetail(id, detailPanelOptions(isFullscreen))}
      />

      {/* Donate-unredeemed pledge — shown only in DONATE mode (rollover off) with a cause chosen,
          mirroring redemption's `config.donateUnredeemed && gift.beneficiary` gate. No footer here. */}
      {!rolloverEnabled && featuredCause && (
        <RedemptionPledgeBanner beneficiary={featuredCause} sender={sender} />
      )}
    </div>
  );
}

/** Product-detail drawer options. Only the fullscreen overlay (fixed inset-0, z-9999) needs the raise;
 *  every other surface omits the key entirely so the drawer stays at MUI's default 1200. */
export function detailPanelOptions(isFullscreen: boolean): { zIndex?: number } {
  return isFullscreen ? { zIndex: LANDING_PREVIEW_DETAIL_Z } : {};
}

/** The step-4 cause the banner features: FIRST selected (mirrors campaigns.beneficiary_product_id),
 *  impact image preferred over primary to match the recipient page. The impact line is the charity's
 *  own impactLine, never the `cause` category. "" counts as absent either way. */
export function toFeaturedCause(
  selectedCauses: CauseSelection[],
  charityCauses: CharityCause[],
): BeneficiaryCause | undefined {
  const first = selectedCauses[0];
  if (!first) return undefined;
  const c = charityCauses.find((x) => x.causeProductId === first.causeProductId);
  if (!c) return undefined;
  return {
    id: c.causeProductId,
    name: c.organization ?? c.title,
    impactLine: c.impactLine || undefined,
    imageUrl: c.impactImageUrl || c.imageUrl || undefined,
    impactIconUrl: c.iconUrl ?? undefined,
    about: c.story ?? undefined,
  };
}
